import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Printer, Eye, EyeOff, ChevronDown, ChevronUp, Save, Loader2, Layers, ListChecks } from 'lucide-react';

/**
 * Print picker — collapsible panel that lets the user pick which sections
 * (or individual fields) of the page print.
 *
 * Backed by EITHER:
 *   - `patientId`+`form`  → backend `/patients/{pid}/print-prefs?form=` (per-patient defaults)
 *   - `storageKey`        → localStorage fallback (page-level defaults)
 *
 * Supports BOTH section- and field-level granularity:
 *   - Sections: items with id `sec-X`     → drives `[data-print-section="sec-X"]`
 *   - Fields:   items with id `fld-X`     → drives `[data-print-field="fld-X"]`
 * (You decide the convention — the picker just matches the data attribute
 * pattern.)
 *
 * Usage:
 *   <PrintPicker
 *     storageKey="objective"
 *     patientId={patientId}      // optional, enables backend persistence
 *     form="objective"           // 'subjective' | 'objective'
 *     groups={[
 *       { title: 'Vitals', items: [
 *         { id: 'sec-vitals', label: 'Whole vitals card', kind: 'section' },
 *         { id: 'fld-bp',    label: 'Blood pressure', kind: 'field' },
 *         { id: 'fld-hr',    label: 'Heart rate', kind: 'field' },
 *       ]},
 *       ...
 *     ]}
 *     onPrint={() => window.print()}
 *   />
 */
export default function PrintPicker({ storageKey, patientId, form,
                                       sections, groups, onPrint }) {
  // Back-compat: legacy `sections` prop maps to a single ungrouped group.
  const effectiveGroups = groups || (sections ? [{
    title: 'Sections',
    items: sections.map(s => ({ id: s.id, label: s.label, kind: 'section' })),
  }] : []);
  const allItems = effectiveGroups.flatMap(g => g.items);

  const [open, setOpen] = useState(false);
  const [picks, setPicks] = useState({});
  const [loadingPrefs, setLoadingPrefs] = useState(!!patientId);
  const [saving, setSaving] = useState(false);
  const [hint, setHint] = useState('');

  // Load: backend prefs (if patientId), else localStorage.
  useEffect(() => {
    if (patientId && form) {
      setLoadingPrefs(true);
      api.get(`/patients/${patientId}/print-prefs`, { params: { form } })
        .then(r => {
          const sel = r.data?.selections || {};
          setPicks(sel);
        })
        .catch(() => { /* swallow, fall back to defaults */ })
        .finally(() => setLoadingPrefs(false));
    } else {
      try {
        const raw = localStorage.getItem(`print-picker-${storageKey}`);
        if (raw) setPicks(JSON.parse(raw));
      } catch { /* ignore */ }
    }
  }, [patientId, form, storageKey]);

  // Persist (fire-and-forget for localStorage; explicit for backend).
  useEffect(() => {
    if (!patientId && storageKey) {
      try { localStorage.setItem(`print-picker-${storageKey}`, JSON.stringify(picks)); }
      catch { /* ignore */ }
    }
  }, [picks, patientId, storageKey]);

  const persistToBackend = async () => {
    if (!patientId || !form) return;
    setSaving(true); setHint('');
    try {
      await api.put(`/patients/${patientId}/print-prefs`, { selections: picks },
                     { params: { form } });
      setHint('Saved');
      setTimeout(() => setHint(''), 1500);
    } catch (e) {
      setHint('Save failed');
    } finally { setSaving(false); }
  };

  const total = allItems.length;
  const selected = allItems.filter(it => picks[it.id] !== false).length;

  // Build CSS — hide unchecked sections AND fields.
  const hiddenSel = allItems
    .filter(it => picks[it.id] === false)
    .map(it => it.kind === 'field'
      ? `[data-print-field="${it.id}"]`
      : `[data-print-section="${it.id}"]`)
    .join(', ');
  const css = hiddenSel ? `@media print { ${hiddenSel} { display: none !important; } }` : '';

  const setAll = (val) => setPicks(Object.fromEntries(allItems.map(it => [it.id, val])));

  return (
    <>
      {css && <style dangerouslySetInnerHTML={{ __html: css }} data-testid="print-picker-css"/>}

      <div className="inline-flex items-center gap-2 print:hidden">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="btn-secondary inline-flex items-center gap-2 text-sm"
          data-testid="print-picker-toggle"
          aria-expanded={open}
        >
          <Eye className="w-4 h-4"/>
          Pick what to print
          <span className="badge-neutral ml-1 text-[10px]">{selected} / {total}</span>
          {open ? <ChevronUp className="w-3.5 h-3.5"/> : <ChevronDown className="w-3.5 h-3.5"/>}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); onPrint && onPrint(); }}
          className="btn-secondary inline-flex items-center gap-2 text-sm"
          data-testid="print-picker-print"
        >
          <Printer className="w-4 h-4"/> Print
        </button>
      </div>

      {open && (
        <div className="mt-3 surface border border-soft rounded-lg shadow-sm p-4 print:hidden max-w-4xl" data-testid="print-picker-panel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <h4 className="font-heading font-semibold text-sm">Print selection</h4>
              <p className="text-[11px] text-fg-muted">
                {patientId
                  ? <>Saved per patient — different patients can have different print defaults.</>
                  : 'Untick anything you don\'t want on the printed copy.'}
              </p>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <button type="button" onClick={() => setAll(true)} className="text-brand hover:underline" data-testid="print-picker-all">All</button>
              <span className="text-fg-muted">·</span>
              <button type="button" onClick={() => setAll(false)} className="text-[#B83A3A] hover:underline" data-testid="print-picker-none">None</button>
              {patientId && (
                <>
                  <span className="text-fg-muted">·</span>
                  <button
                    type="button"
                    onClick={persistToBackend}
                    disabled={saving || loadingPrefs}
                    className="text-brand inline-flex items-center gap-1 hover:underline"
                    data-testid="print-picker-save"
                  >
                    {saving ? <Loader2 className="w-3 h-3 animate-spin"/> : <Save className="w-3 h-3"/>}
                    {saving ? 'Saving…' : 'Save for this patient'}
                  </button>
                  {hint && <span className="text-[11px] text-green-700 font-semibold">✓ {hint}</span>}
                </>
              )}
            </div>
          </div>

          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {effectiveGroups.map((g, gi) => (
              <div key={gi}>
                {g.title && (
                  <div className="text-[11px] uppercase tracking-wider text-fg-muted mb-1 flex items-center gap-1.5">
                    <Layers className="w-3 h-3"/> {g.title}
                  </div>
                )}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {g.items.map(it => {
                    const on = picks[it.id] !== false;
                    return (
                      <label
                        key={it.id}
                        className={`flex items-center gap-2 px-3 py-2 rounded-md border text-xs cursor-pointer transition ${
                          on ? 'border-brand/40 bg-brand/5' : 'border-soft text-fg-muted'
                        } ${it.kind === 'field' ? 'pl-6' : ''}`}
                        data-testid={`print-pick-${it.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => setPicks(p => ({ ...p, [it.id]: !on }))}
                          className="accent-brand"
                        />
                        {it.kind === 'field'
                          ? <ListChecks className={`w-3.5 h-3.5 ${on ? 'text-brand' : ''}`}/>
                          : (on ? <Eye className="w-3.5 h-3.5 text-brand"/> : <EyeOff className="w-3.5 h-3.5"/>)}
                        <span className="flex-1 truncate">{it.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
